import Typography from '@mui/material/Typography';
import { DataGrid, GridColDef, useGridApiRef } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import { useDialogs } from '@toolpad/core/useDialogs';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { useParams } from 'react-router';

import { useGetCoursePeriod } from 'api/hooks/private/periods/GetCoursePeriod';
import {
  CoursePeriodEnrollment,
  useGetCoursePeriodEnrollments,
} from 'api/hooks/private/periods/GetCoursePeriodEnrollments';
import { AttendanceValue, TakeAttendanceParams, useTakeAttendance } from 'api/hooks/private/periods/TakeAttendance';

import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import PageContainer from 'components/PageContainer';

import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import { renderAttendance, renderEditAttendance } from './cell-renderers/attendance';
import CoursePeriodRosterToolbar from './components/CoursePeriodRosterToolbar';

dayjs.extend(calendar);
dayjs.extend(localizedFormat);

const columns: GridColDef<CoursePeriodEnrollment>[] = [
  {
    field: 'period_number',
    type: 'number',
    headerName: 'Period #',
    headerAlign: 'left',
    align: 'left',
    minWidth: 100,
  },
  {
    field: 'last_name',
    type: 'string',
    headerName: 'Last Name',
    minWidth: 150,
  },
  {
    field: 'first_name',
    type: 'string',
    headerName: 'First Name',
    minWidth: 150,
    valueFormatter: (_value, row) => {
      let formattedValue = row.first_name;
      if (row.nickname) {
        return (formattedValue += ` (${row.nickname})`);
      }
      return formattedValue;
    },
  },
  {
    field: 'middle_name',
    type: 'string',
    headerName: 'Middle Name',
    minWidth: 150,
  },
  {
    field: 'attendance',
    type: 'singleSelect',
    headerName: 'Attendance',
    renderCell: renderAttendance,
    renderEditCell: renderEditAttendance,
    // valueOptions: ATTENDANCE_VALUES,
    width: 150,
    editable: true,
  },
  {
    field: 'attendance_taken_at',
    headerName: 'Attendance Taken At',
    type: 'dateTime',
    sortable: false,
    width: 200,
    valueGetter: (value) => (!value ? null : new Date(value)),
    renderCell: (params) => {
      const enrollment = params.api.getRow(params.id);
      if (!enrollment.attendance_taken_at) {
        return;
      }
      return (
        <Typography variant="body2">
          {enrollment.attendance_taken_by_user.id && (
            <>
              {`${enrollment.attendance_taken_by_user?.nickname ?? enrollment.attendance_taken_by_user?.first_name ?? ''} ${enrollment.attendance_taken_by_user?.last_name ?? ''}`}
              <br />
            </>
          )}
          {`${dayjs(enrollment.attendance_taken_at).calendar(null, {
            sameElse: 'MMM D, YYYY [at] h:mm a',
          })}`}
        </Typography>
      );
    },
  },
  {
    field: 'canceled_at',
    headerName: 'Canceled',
    type: 'dateTime',
    sortable: false,
    width: 225,
    valueGetter: (value) => (!value ? null : new Date(value)),
    renderCell: (params) => {
      const enrollment = params.api.getRow(params.id);
      if (!enrollment.canceled_at) {
        return;
      }
      return (
        <Typography variant="body2">
          {enrollment.canceled_by_user.id && (
            <>
              {`${enrollment.canceled_by_user?.nickname ?? enrollment.canceled_by_user?.first_name ?? ''} ${enrollment.canceled_by_user?.last_name ?? ''}`}
              <br />
            </>
          )}
          {`${dayjs(enrollment.canceled_at).calendar(null, {
            sameElse: 'MMM D, YYYY [at] h:mm a',
          })}`}
        </Typography>
      );
    },
  },
];

export function CoursePeriodRoster() {
  // get the course period id
  const { id } = useParams<{ id: string }>();
  const dialogs = useDialogs();
  const apiRef = useGridApiRef();
  const queryClient = useQueryClient();
  const takeAttendance = useTakeAttendance();

  // get course period
  const period = useGetCoursePeriod({
    variables: {
      id: id ?? '',
    },
    enabled: !!id,
  });

  // get course period enrollments
  const enrollments = useGetCoursePeriodEnrollments({
    variables: {
      id: id ?? '',
    },
    enabled: !!id,
  });

  // puts all attendance cells in edit mode
  const handleStartAttendance = () => {
    // get all row ids
    const ids = apiRef.current?.getAllRowIds() ?? [];

    // start cell edit mode for all attendance cells
    ids.forEach((id) => apiRef.current?.startCellEditMode({ id: id, field: 'attendance' }));
  };

  // discards all attendance changes
  const handleCancelAttendance = () => {
    // get all row ids
    const ids = apiRef.current?.getAllRowIds() ?? [];

    // stop cell edit mode for all attendance cells in edit mode and discard changes
    ids.forEach((id) => {
      if (apiRef.current?.getCellMode(id, 'attendance') === 'edit') {
        apiRef.current.stopCellEditMode({ id: id, field: 'attendance', ignoreModifications: true });
      }
    });
  };

  const handleSaveAttendance = async () => {
    // ensure the course period id is not undefined
    if (!id) return;

    // store the updated attendance data
    const newAttendanceData: TakeAttendanceParams = {
      coursePeriodId: id,
      attendanceRecords: [],
    };

    try {
      // get all row ids
      const ids = apiRef.current?.getAllRowIds() ?? [];

      // for each row
      ids.forEach((id) => {
        // get the updated row value
        const updatedRow = apiRef.current?.getRowWithUpdatedValues(id, 'attendance');

        // push the new attendance record to the array
        newAttendanceData.attendanceRecords.push({
          studentId: id as string,
          attendance: updatedRow?.attendance ?? null,
        });
      });

      // update attendance for the course period
      const result = await takeAttendance.mutateAsync(newAttendanceData);

      if (result) {
        // for each row
        ids.forEach((id) => {
          // if the cell is in edit mode
          if (apiRef.current?.getCellMode(id, 'attendance') === 'edit') {
            // stop cell edit mode
            apiRef.current.stopCellEditMode({ id: id, field: 'attendance', ignoreModifications: true });
          }

          // if the row is in edit mode
          if (apiRef.current?.getRowMode(id) === 'edit') {
            // stop cell edit mode
            apiRef.current.stopRowEditMode({ id: id, field: 'attendance', ignoreModifications: true });
          }
        });

        // invalidate related queries
        queryClient.invalidateQueries({
          queryKey: ['course_period', id],
        });
        queryClient.invalidateQueries({
          queryKey: ['course_periods'],
        });
        queryClient.invalidateQueries({
          queryKey: ['course_period_enrollments'],
        });

        // show the success dialog
        dialogs.open(FeedbackDialog, {
          title: 'Success',
          message: 'Attendance was saved successfully!',
          icon: 'success',
        });
      }
    } catch (error) {
      // show the error dialog
      dialogs.open(FeedbackDialog, {
        title: 'Error',
        message: getApiErrorMessage(error),
      });
    }
  };

  const handleBulkEditAttendance = (value: AttendanceValue) => {
    // get all row ids
    const ids = apiRef.current?.getAllRowIds() ?? [];

    // set the value for each cell in edit mode
    ids.forEach((id) => {
      if (apiRef.current?.getCellMode(id, 'attendance') === 'edit') {
        apiRef.current?.setEditCellValue({
          id: id,
          field: 'attendance',
          value: value,
        });
      }
    });
  };

  return (
    <PageContainer
      title={period.data?.course_section?.title}
      subtitle={`${dayjs(period.data?.start).format('LLL')} - ${dayjs(period.data?.end).format('LT')}`}
    >
      <DataGrid
        apiRef={apiRef}
        loading={enrollments.isFetching || takeAttendance.isPending}
        columns={columns}
        rows={enrollments.data}
        getRowId={(row) => row.student_user_id}
        editMode="row"
        showToolbar
        slots={{
          toolbar: CoursePeriodRosterToolbar,
        }}
        slotProps={{
          toolbar: {
            handleStartAttendance,
            handleCancelAttendance,
            handleSaveAttendance,
            handleBulkEditAttendance,
          },
        }}
        onCellDoubleClick={(_params, event) => {
          event.defaultMuiPrevented = true;
        }}
        onCellKeyDown={(_params, event) => {
          // prevent enter key from starting edit mode
          if (event.key === 'Enter') {
            event.defaultMuiPrevented = true;
          }
        }}
        disableRowSelectionOnClick
      />
    </PageContainer>
  );
}

export default CoursePeriodRoster;
